import React, { useState } from "react";
import { IoIosArrowForward } from "react-icons/io";
import { useLanguage } from "../../i18n/LanguageContext";

const DropDownContext =
  "mb-4 rounded-2xl border border-blue/10 bg-white shadow-sm shadow-blue/5 transition-all duration-300 hover:shadow-md hover:border-blue/20 overflow-hidden";
const TitleOpen =
  "flex items-center w-full text-left gap-3 px-5 py-4 text-lg lg:text-xl font-bold text-orange transition-colors duration-200";
const TitleClose =
  "flex items-center w-full text-left gap-3 px-5 py-4 text-lg lg:text-xl font-bold text-blue hover:text-orange transition-colors duration-200";
const TextMain = "text-sm lg:text-base text-darkBlue/70 leading-relaxed mt-3";
const SubTitle = "text-base lg:text-lg mt-4 font-bold text-blue";
const TextDot = "list-disc text-sm lg:text-base text-darkBlue/70 ml-5 space-y-1";

function UsecaseItem({ item, withSections = false }) {
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();

  return (
    <div className={DropDownContext}>
      <button
        type="button"
        className={open ? TitleOpen : TitleClose}
        onClick={() => setOpen((v) => !v)}
      >
        <IoIosArrowForward
          className={`text-lg shrink-0 transition-transform duration-300 ${open ? "rotate-90" : ""}`}
        />
        {item.title}
      </button>
      <div className={open ? "px-5 pb-5" : "hidden"}>
        {item.p1 && <p className={TextMain}>{item.p1}</p>}
        {item.p2 && <p className={TextMain}>{item.p2}</p>}

        {withSections && (
          <>
            <h4 className={SubTitle}>{t("usecases.sectionLabels.automate")}</h4>
            <ul className={TextDot}>
              {(item.automate || []).map((line, idx) => (
                <li key={`a-${idx}`}>{line}</li>
              ))}
            </ul>

            <h4 className={SubTitle}>{t("usecases.sectionLabels.reduce")}</h4>
            <ul className={TextDot}>
              {(item.reduce || []).map((line, idx) => (
                <li key={`r-${idx}`}>{line}</li>
              ))}
            </ul>

            <h4 className={SubTitle}>{t("usecases.sectionLabels.achieve")}</h4>
            <ul className={TextDot}>
              {(item.achieve || []).map((line, idx) => (
                <li key={`c-${idx}`}>{line}</li>
              ))}
            </ul>
          </>
        )}

        {item.bullets && (
          <ul className={TextDot}>
            {item.bullets.map((line, idx) => (
              <li key={`b-${idx}`}>{line}</li>
            ))}
          </ul>
        )}

        {item.p3 && <p className={TextMain}>{item.p3}</p>}
      </div>
    </div>
  );
}

const Usecases = () => {
  const { t } = useLanguage();

  const leftItems = [
    t("usecases.list.international", {}),
    t("usecases.list.tourism", {}),
    t("usecases.list.finance", {}),
    t("usecases.list.manufacturing", {}),
    t("usecases.list.oil", {}),
    t("usecases.list.retail", {}),
    t("usecases.list.ecommerce", {}),
    t("usecases.list.transport", {}),
  ];

  const rightItems = [
    t("usecases.list.foreignWorker", {}),
    t("usecases.list.thaiCorp", {}),
    t("usecases.list.ipo", {}),
    t("usecases.list.franchise", {}),
    t("usecases.list.realEstate", {}),
    t("usecases.list.healthcare", {}),
  ];

  return (
    <div className="mt-[65px] lg:mt-[80px] min-h-screen pb-20 md:pb-0 bg-gradient-to-b from-white to-gray-50/50">
      <div className="mx-auto sm:max-w-3xl lg:max-w-screen-2xl 2xl:max-w-screen-2xl px-5 md:px-8">
        <div className="w-full text-center pt-16 pb-4">
          <p className="text-sm md:text-base font-semibold uppercase tracking-widest text-orange mb-3">
            {t("usecases.label")}
          </p>
          <h1 className="text-blue text-3xl md:text-4xl lg:text-5xl font-bold">
            {t("usecases.title")}
          </h1>
          <div className="mt-4 mx-auto w-20 h-1 rounded-full bg-gradient-to-r from-orange to-pink" />
          <p className="mt-6 text-darkBlue/60 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            {t("usecases.subtitle")}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 grid-cols-1 gap-6 mt-10">
          <div>
            {leftItems.map((item, idx) => (
              <UsecaseItem key={`l-${idx}`} item={item} withSections={idx === 0} />
            ))}
          </div>

          <div>
            {rightItems.map((item, idx) => (
              <UsecaseItem key={`r-${idx}`} item={item} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Usecases;
